import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import { useAuth, ROLE_ROUTES } from "../../../contexts/AuthContext";
import api from "../../../lib/api";

export default function LoginForm() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "", rememberMe: false });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  const validate = () => {
    const errs = {};
    if (!formData?.email) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) errs.email = "Enter a valid email";
    if (!formData?.password) errs.password = "Password is required";
    else if (formData?.password?.length < 6) errs.password = "Password must be at least 6 characters";
    return errs;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (authError) setAuthError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setIsLoading(true);
    setAuthError("");

    const { data, error } = await signIn(formData?.email, formData?.password);

    if (error) {
      setIsLoading(false);
      setAuthError(error?.message || "Invalid email or password. Please try again.");
      return;
    }

    const role = data?.user?.role || 'vendor';
    let route = ROLE_ROUTES?.[role] || '/gym-owner-portal';

    // Vendors with incomplete setup go to gym setup first
    if (role === 'vendor') {
      try {
        const { data: settings } = await api.get('/gym-settings');
        if (!settings || !settings.setup_completed) route = '/gym-setup';
      } catch {
        route = '/gym-setup';
      }
    }

    setIsLoading(false);
    navigate(route);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {authError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <Icon name="AlertCircle" size={16} color="#f87171" />
          <p className="text-xs text-red-400 font-caption">{authError}</p>
        </div>
      )}
      {/* Email */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-300 font-caption">Email address</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Icon name="Mail" size={16} />
          </div>
          <input
            type="email"
            name="email"
            value={formData?.email}
            onChange={handleChange}
            placeholder="you@gymname.com"
            className={`w-full h-11 pl-10 pr-4 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all ${
              errors?.email ? "border-red-500/60" : "border-white/10 focus:border-teal-500/50"
            }`}
          />
        </div>
        {errors?.email && <p className="text-xs text-red-400 font-caption">{errors?.email}</p>}
      </div>
      {/* Password */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-300 font-caption">Password</label>
          <a href="/forgot-password-screen" className="text-xs text-teal-400 hover:text-teal-300 transition-colors font-caption">
            Forgot password?
          </a>
        </div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Icon name="Lock" size={16} />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData?.password}
            onChange={handleChange}
            placeholder="Enter your password"
            className={`w-full h-11 pl-10 pr-11 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all ${
              errors?.password ? "border-red-500/60" : "border-white/10 focus:border-teal-500/50"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>
        {errors?.password && <p className="text-xs text-red-400 font-caption">{errors?.password}</p>}
      </div>
      {/* Remember Me */}
      <div className="flex items-center gap-2">
        {/* <input
          type="checkbox"
          id="rememberMe"
          name="rememberMe"
          checked={formData?.rememberMe}
          onChange={handleChange}
          className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/50 focus:ring-offset-0"
        />
        <label htmlFor="rememberMe" className="text-sm text-slate-400 font-body cursor-pointer">
          Keep me signed in for 30 days
        </label> */}
      </div>
      
      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold text-sm font-caption transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-teal-900/30"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Signing in...
          </>
        ) : (
          <>
            Sign In
            <Icon name="ArrowRight" size={16} />
          </>
        )}
      </button>
    </form>
  );
}
